import { z } from 'zod';
import type { SitePage, SiteTheme } from '../domains/site/schema';
import { getThemePresentation } from './theme-presentation';

const bridgeSectionFieldSchema = z.enum([
  'eyebrow',
  'headline',
  'subhead',
  'ctaLabel',
  'title',
  'body',
  'siteName',
  'companyName',
  'description',
  'goalLabel',
  'pageColumnTitle',
  'legalColumnTitle',
  'supportColumnTitle',
  'supportFallbackMessage',
]);

const bridgeItemFieldSchema = z.enum([
  'title',
  'body',
  'label',
  'value',
  'question',
  'answer',
  'name',
  'role',
  'comment',
  'price',
]);

const bridgeSectionTextOverrideSchema = z.object({
  sectionId: z.string().min(1),
  field: bridgeSectionFieldSchema,
  value: z.string(),
});

const bridgeItemTextOverrideSchema = z.object({
  sectionId: z.string().min(1),
  itemId: z.string().min(1),
  field: bridgeItemFieldSchema,
  value: z.string(),
});

const bridgeThemeTokenOverridesSchema = z.object({
  text: z.string().min(1).optional(),
  textMuted: z.string().min(1).optional(),
  background: z.string().min(1).optional(),
  surface: z.string().min(1).optional(),
  surfaceMuted: z.string().min(1).optional(),
  border: z.string().min(1).optional(),
  primary: z.string().min(1).optional(),
  secondary: z.string().min(1).optional(),
}).default({});

const bridgePresentationOverrideFieldsSchema = z.object({
  archetypeId: z.string().min(1).optional(),
  sectionHeadingStyle: z.enum(['center_bar', 'left_rule', 'center_panel']).optional(),
  cardStyle: z.enum(['outline', 'surface', 'tint', 'elevated']).optional(),
  cardShadow: z.enum(['none', 'soft', 'medium', 'strong']).optional(),
  borderEmphasis: z.enum(['none', 'soft', 'normal', 'strong']).optional(),
  cardRadius: z.enum(['sharp', 'soft', 'rounded', 'extra_rounded']).optional(),
  spacingScale: z.enum(['compact', 'normal', 'spacious']).optional(),
  heroLayoutStyle: z.enum(['authority_band', 'story_split', 'product_frame', 'editorial_centered']).optional(),
  proofLayoutStyle: z.enum(['grid', 'band', 'pill']).optional(),
  reviewLayoutStyle: z.enum(['cards', 'speech', 'editorial']).optional(),
  ctaLayoutStyle: z.enum(['panel', 'band', 'contrast']).optional(),
});

const bridgePresentationOverridesSchema = bridgePresentationOverrideFieldsSchema.default({});

const bridgeSectionPresentationOverrideSchema = bridgePresentationOverrideFieldsSchema.extend({
  sectionId: z.string().min(1),
});

export const bridgeRouteOverrideSchema = z.object({
  pageTitle: z.string().optional(),
  hiddenSectionIds: z.array(z.string().min(1)).default([]),
  sectionOrder: z.array(z.string().min(1)).default([]),
  themeTokenOverrides: bridgeThemeTokenOverridesSchema,
  presentationOverrides: bridgePresentationOverridesSchema,
  sectionPresentationOverrides: z.array(bridgeSectionPresentationOverrideSchema).default([]),
  sectionTextOverrides: z.array(bridgeSectionTextOverrideSchema).default([]),
  itemTextOverrides: z.array(bridgeItemTextOverrideSchema).default([]),
});

export type BridgeRouteOverride = z.infer<typeof bridgeRouteOverrideSchema>;

const bridgeMarkerPattern = /\/\*\s*@chatlp-bridge\s*([\s\S]*?)\*\//u;

export function formatBridgeRouteOverrideComment(
  override: Partial<BridgeRouteOverride> = {},
) {
  const normalized = bridgeRouteOverrideSchema.parse(override);

  return [
    '/* @chatlp-bridge',
    JSON.stringify(normalized, null, 2),
    '*/',
  ].join('\n');
}

export function parseBridgeRouteOverrideFromFileContent(content: string): BridgeRouteOverride | null {
  const match = content.match(bridgeMarkerPattern);
  if (!match?.[1]) return null;

  try {
    const parsed = JSON.parse(match[1]);
    const result = bridgeRouteOverrideSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function setStringField(target: Record<string, unknown>, field: string, value: string) {
  if (typeof target[field] === 'string') {
    target[field] = value;
  }
}

function clonePage(page: SitePage): SitePage {
  return JSON.parse(JSON.stringify(page)) as SitePage;
}

function cloneTheme(theme: SiteTheme): SiteTheme {
  return JSON.parse(JSON.stringify(theme)) as SiteTheme;
}

function reorderPageSections(
  page: SitePage,
  hiddenSectionIds: readonly string[],
  sectionOrder: readonly string[],
) {
  const hiddenIdSet = new Set(hiddenSectionIds);
  const visibleSections = page.sections.filter((section) => !hiddenIdSet.has(section.id));

  if (sectionOrder.length === 0) {
    return visibleSections;
  }

  const remainingSections = [...visibleSections];
  const orderedSections = sectionOrder.flatMap((sectionId) => {
    const index = remainingSections.findIndex((section) => section.id === sectionId);
    if (index < 0) return [];
    return remainingSections.splice(index, 1);
  });

  return [...orderedSections, ...remainingSections];
}

export function applyBridgeRouteOverride(
  page: SitePage,
  override: BridgeRouteOverride,
): SitePage {
  const nextPage = clonePage(page);

  if (override.pageTitle && override.pageTitle.trim()) {
    nextPage.title = override.pageTitle;
  }

  for (const sectionOverride of override.sectionTextOverrides) {
    const section = nextPage.sections.find((candidate) => candidate.id === sectionOverride.sectionId);
    if (!section) continue;
    setStringField(section as unknown as Record<string, unknown>, sectionOverride.field, sectionOverride.value);
  }

  for (const itemOverride of override.itemTextOverrides) {
    const section = nextPage.sections.find((candidate) => candidate.id === itemOverride.sectionId);
    if (!section || !('items' in section) || !Array.isArray(section.items)) continue;

    const item = section.items.find((candidate) => (
      typeof candidate === 'object'
      && candidate !== null
      && 'id' in candidate
      && candidate.id === itemOverride.itemId
    ));
    if (!item || typeof item !== 'object') continue;

    setStringField(item as Record<string, unknown>, itemOverride.field, itemOverride.value);
  }

  nextPage.sections = reorderPageSections(
    nextPage,
    override.hiddenSectionIds,
    override.sectionOrder,
  );

  return nextPage;
}

export function applyBridgeThemeOverride(
  theme: SiteTheme,
  override: BridgeRouteOverride,
): SiteTheme {
  const nextTheme = cloneTheme(theme);

  if (Object.keys(override.themeTokenOverrides).length > 0) {
    nextTheme.tokens = {
      ...nextTheme.tokens,
      ...override.themeTokenOverrides,
    };
  }

  if (Object.keys(override.presentationOverrides).length > 0) {
    nextTheme.presentation = {
      ...getThemePresentation(nextTheme),
      ...override.presentationOverrides,
    };
  }

  return nextTheme;
}

function applyPresentationOverrideToTheme(
  theme: SiteTheme,
  presentationOverride: z.infer<typeof bridgePresentationOverrideFieldsSchema>,
): SiteTheme {
  const nextTheme = cloneTheme(theme);
  const mergedPresentation = {
    ...getThemePresentation(nextTheme),
    ...presentationOverride,
  };

  nextTheme.presentation = getThemePresentation({
    ...nextTheme,
    presentation: mergedPresentation,
  });

  return nextTheme;
}

export function applyBridgeSectionThemeOverride(
  theme: SiteTheme,
  override: BridgeRouteOverride,
  sectionId: string,
): SiteTheme {
  const matchingOverrides = override.sectionPresentationOverrides.filter((entry) => entry.sectionId === sectionId);

  if (matchingOverrides.length === 0) {
    return theme;
  }

  return matchingOverrides.reduce<SiteTheme>((currentTheme, entry) => {
    const presentationOverride = { ...entry };
    delete presentationOverride.sectionId;
    return applyPresentationOverrideToTheme(currentTheme, presentationOverride);
  }, theme);
}
